import React from 'react';

import Icon from '@ant-design/icons';
import cx from 'classnames';
import { connect } from 'react-redux';
import { Button, Input, InputNumber, Modal } from 'antd';
import moment from 'moment';
import { isEqual } from 'lodash';
import { RouteComponentProps, withRouter } from 'react-router';

import { selectors as accountListSelectors } from '../../../../../slices/accounts/list';
import { selectors as authSelectors } from '../../../../../slices/auth';
import { ReactComponent as CrossSVG } from '../../../../../assets/sightx-icons/svg/x-close.svg';
import { ReactComponent as CheckCircleSVG } from '../../../../../assets/sightx-icons/svg/sx-check-circle.svg';
import { Features, isFeatureEnabled } from '../../../../../featureToggles/features';
import { InputFilter } from '../../../../common/filters/interfaces';
import { RootState } from '../../../../../slices/rootReducer';
import { IPreviewProratedEnterpriseLicenseCost, IPreviewProratedEnterpriseLicenseCostParams } from '../../../../../slices/accounts/previewProratedEnterpriseLicenseCost/interfaces';
import { fetchPreviewProratedEnterpriseLicenseCost } from '../../../../../slices/accounts/previewProratedEnterpriseLicenseCost/thunk';
import { selectors as proratedEnterpriseLicenseCostSelectors } from '../../../../../slices/accounts/previewProratedEnterpriseLicenseCost/selectors';
import { actions as proratedEnterpriseLicenseCostActions } from '../../../../../slices/accounts/previewProratedEnterpriseLicenseCost';
import { selectors as addEnterpriseSeatsSelectors } from '../../../../../slices/accounts/addEnterpriseSeats/selectors';
import { addEnterpriseSeats } from '../../../../../slices/accounts/addEnterpriseSeats/thunk';
import { IAddEnterpriseSeatsDTO } from '../../../../../slices/accounts/addEnterpriseSeats/interfaces';
import { AddUsersInBulkForm } from '../../../../users/common/addUsersInBulkForm';
import { IUserBulkCreationPayload, IUserForBulkCreation, createUsersInBulk } from '../../../../../slices/users/bulkCreation/index.thunks';
import { selectors as bulkSelectors } from '../../../../../slices/users/bulkCreation';
import { FeatureToggle, Off, On } from '../../../../../featureToggles';
import { SxEmailTags } from '../../../../common/SxEmailTags';

import styles from './index.module.scss';

const isTrialsOfTiersEnabled = isFeatureEnabled(Features.TrialsOfTiers);
const {
  selectPreviewProratedEnterpriseLicenseCost,
  selectLoadingPreviewProratedEnterpriseLicenseCost
} = proratedEnterpriseLicenseCostSelectors;
const {
  resetValues
} = proratedEnterpriseLicenseCostActions;
const {
  getLoading,
  getAccountById
} = accountListSelectors;
const {
  selectLoadingAddEnterpriseSeats
} = addEnterpriseSeatsSelectors;
const {
  getLoading: getBulkLoading
} = bulkSelectors;

enum ModalSteps {
  Seats = 'seats',
  Summary = 'summary',
  Users = 'users'
}

interface IMapStateToProps {
  accountId: string | undefined;
  currentSeats: number;
  renewalDate: string;
  billingEmail: string | undefined;
  isSendingInvites: boolean;
  isFetchingAccount: boolean;
  isAddingEnterpriseSeats: boolean;
  isFetchingProratedEnterpriseLicenseCost: boolean;
  proratedEnterpriseLicenseCost: IPreviewProratedEnterpriseLicenseCost | undefined;
}

interface IDispatchProps {
  addEnterpriseSeats: (params: IAddEnterpriseSeatsDTO) => void;
  createUsersInBulk: (params: IUserBulkCreationPayload) => void;
  resetProratedEnterpriseLicenseCostValues: () => void;
  fetchPreviewProratedEnterpriseLicenseCost: (params: IPreviewProratedEnterpriseLicenseCostParams) => void;
}

interface AddSeatsEnterpriseModalState {
  numberOfSeats: number;
  invoiceNotes?: string;
  isAddingSeats: boolean;
  step: ModalSteps;
  usersToAddList: IUserForBulkCreation[];
  // use in Features.BillingUserFacing
  invoicedEmails: string[];
  // end of use in Features.BillingUserFacing
}

interface AddSeatsEnterpriseModalProps {
  onCancel: () => void;
}

type IProps = AddSeatsEnterpriseModalProps & IMapStateToProps & IDispatchProps & RouteComponentProps;

const isBillingUserFacingEnv = isFeatureEnabled(Features.BillingUserFacing);

class AddSeatsEnterpriseModalBase extends React.PureComponent<IProps, AddSeatsEnterpriseModalState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      step: ModalSteps.Seats,
      numberOfSeats: 1,
      isAddingSeats: false,
      usersToAddList: [],
      invoicedEmails: []
    };
    this.handleNext = this.handleNext.bind(this);
    this.getButtonLabel = this.getButtonLabel.bind(this);
    this.handleSeatsNumber = this.handleSeatsNumber.bind(this);
    this.handleUserListChange = this.handleUserListChange.bind(this);
    this.handleInvoiceNotesChange = this.handleInvoiceNotesChange.bind(this);
    this.handleSetInvoicedEmails = this.handleSetInvoicedEmails.bind(this);
  }

  componentDidMount(): void {
    const { billingEmail } = this.props;
    if (isFeatureEnabled(Features.BillingUserFacing) && billingEmail) {
      this.setState({ invoicedEmails: [billingEmail] });
    }
  }

  componentDidUpdate(prevProps: Readonly<IProps>): void {
    const {
      isSendingInvites: prevIsSendingInvites,
      isFetchingAccount: prevFetchingAccount,
      proratedEnterpriseLicenseCost: prevProratedEnterpriseLicenseCost
    } = prevProps;
    const { proratedEnterpriseLicenseCost, isAddingEnterpriseSeats, isFetchingAccount, isSendingInvites } = this.props;
    const { isAddingSeats, step } = this.state;
    if (proratedEnterpriseLicenseCost && !isEqual(proratedEnterpriseLicenseCost, prevProratedEnterpriseLicenseCost)) {
      this.setState({ step: ModalSteps.Summary });
    }

    if (
      isFetchingAccount !== prevFetchingAccount &&
      !isFetchingAccount &&
      proratedEnterpriseLicenseCost &&
      !isAddingEnterpriseSeats &&
      isAddingSeats
    ) {
      this.setState({ step: ModalSteps.Users, isAddingSeats: false });
    }

    if (step === ModalSteps.Users && prevIsSendingInvites !== isSendingInvites && !isSendingInvites) {
      this.props.history.push('/settings/users');
    }
  }

  componentWillUnmount(): void {
    this.props.resetProratedEnterpriseLicenseCostValues();
  }

  private handleSetInvoicedEmails(values: string[]) {
    this.setState({ invoicedEmails: values });
  }

  private getButtonLabel(): string {
    let buttonLabel = '';
    switch(this.state.step) {
    case ModalSteps.Seats:
      buttonLabel = 'Next';
      break;
    case ModalSteps.Summary:
      buttonLabel = 'Add seats';
      break;
    case ModalSteps.Users:
      buttonLabel = 'Send';
      break;
    }
    return buttonLabel;
  }

  private handleUserListChange(usersToAddList: IUserForBulkCreation[]) {
    this.setState({ usersToAddList });
  }

  private handleInvoiceNotesChange(event: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ invoiceNotes: event.target.value });
  }

  private handleSeatsNumber(value: InputFilter) {
    if (typeof value !== 'number') {
      return;
    }
    this.setState({ numberOfSeats: value });
  }

  private handleNext() {
    const { step, numberOfSeats, invoiceNotes, usersToAddList, invoicedEmails } = this.state;
    const { accountId, fetchPreviewProratedEnterpriseLicenseCost, addEnterpriseSeats, createUsersInBulk, billingEmail } = this.props;
    if (!accountId) {
      return;
    }

    switch(step) {
    case ModalSteps.Seats:
      fetchPreviewProratedEnterpriseLicenseCost({ accountId, numberOfSeats });
      break;
    case ModalSteps.Summary: {
      const recipientEmails: string[] = invoicedEmails.filter((email) => email !== billingEmail);
      const carbonCopyRecipients = isBillingUserFacingEnv && recipientEmails.length ? recipientEmails : undefined;
      this.setState({ isAddingSeats: true });
      addEnterpriseSeats({
        accountId,
        numberOfSeats,
        invoiceNotes,
        carbonCopyRecipients
      });
      break;
    }
    case ModalSteps.Users:
      createUsersInBulk({ accountId, users: usersToAddList });
      break;
    }
  }

  private renderFooter() {
    const { onCancel, isFetchingProratedEnterpriseLicenseCost, isFetchingAccount, isAddingEnterpriseSeats, isSendingInvites } = this.props;
    const { step, usersToAddList } = this.state;
    const isOnUsersStep = step === ModalSteps.Users;
    const backButtonLabel = isOnUsersStep ? 'Skip for now' : 'Cancel';
    const shouldDisableButton = isFetchingProratedEnterpriseLicenseCost ||
      isAddingEnterpriseSeats ||
      (isOnUsersStep && !usersToAddList.length) ||
      isFetchingAccount ||
      isSendingInvites;

    return (
      <div className={styles.actions}>
        <Button className={styles.button} onClick={onCancel}>{backButtonLabel}</Button>
        <Button
          type='primary'
          className={styles.button}
          disabled={shouldDisableButton}
          onClick={this.handleNext}
        >
          {this.getButtonLabel()}
        </Button>
      </div>
    );
  }

  private renderAlert () {
    if (this.state.step !== ModalSteps.Users) {
      return;
    }
    return <div className={styles.alert}><Icon component={CheckCircleSVG} /> <span>Seats successfully added</span></div>;
  }

  private renderTitle() {
    const title = this.state.step === ModalSteps.Users ? 'Assign users to available seats' : 'Update Subscription';
    return (
      <div className={styles.title}>{title}</div>
    );
  }

  private renderAddSeats() {
    const { numberOfSeats } = this.state;
    const { currentSeats, isFetchingProratedEnterpriseLicenseCost } = this.props;

    return (
      <div className={styles.content}>
        <h2 className={styles.subTitle}>Add seats</h2>
        <div className={styles.row}>
          <p>How many seats would you like to add to your account?</p>
          <InputNumber
            min={1}
            max={10000}
            disabled={isFetchingProratedEnterpriseLicenseCost}
            value={numberOfSeats}
            className={styles.seatsInput}
            onChange={this.handleSeatsNumber}
          />
        </div>
        <div className={cx(styles.resultBox, styles.seats)}>
          <p><b>{numberOfSeats}</b> additional seat will be added, bringing you up to <b>{currentSeats + numberOfSeats}</b> seats in total</p>
        </div>
      </div>
    );
  }

  private renderSummary() {
    const { renewalDate, proratedEnterpriseLicenseCost, isAddingEnterpriseSeats } = this.props;
    const { numberOfSeats } = this.state;
    const text = ` new user${numberOfSeats > 1 ? 's' : ''}`;

    if (!proratedEnterpriseLicenseCost) {
      return null;
    }

    const { proratedCostPerSeat, totalCost } = proratedEnterpriseLicenseCost;
    return (
      <>
        <div className={styles.content}>
          <h2 className={styles.subTitle}>Invoice summary</h2>
          <p>You will be charged a prorated fee for added seats based on the days left in your current subscription, which renews on <b>{renewalDate}</b>.</p>
          <div className={styles.resultBox}>
            <div className={styles.row}>
              <span>Prorated subscription (${proratedCostPerSeat.toFixed(2)} x {numberOfSeats}{text})</span>
              <span>${totalCost.toFixed(2)}</span>
            </div>
            <hr/>
            <div className={styles.row}>
              <span><b>Today’s total</b></span>
              <span><b>${totalCost.toFixed(2)}</b></span>
            </div>
          </div>
        </div>
        <div className={styles.content}>
          <h2 className={styles.subTitle}>Invoice information</h2>
          <label>PO # / Invoice notes</label>
          <Input
            maxLength={30}
            disabled={isAddingEnterpriseSeats}
            placeholder="Add notes to be included on the invoice"
            onChange={this.handleInvoiceNotesChange}
          />
          <FeatureToggle feature={Features.BillingUserFacing}>
            <On>
              <div className={styles.invoicedEmails}>
                <label>Send invoice to</label>
                <SxEmailTags
                  emails={this.state.invoicedEmails}
                  setEmails={this.handleSetInvoicedEmails}
                  maxAllowed={10}
                  disabled={isAddingEnterpriseSeats}
                  withReadOnly
                />
              </div>
            </On>
            <Off></Off>
          </FeatureToggle>
        </div>
      </>
    );
  }

  private renderAddUsersInBulk() {
    const { accountId } = this.props;
    return (
      <div className={styles.bulkWrapper}>
        <AddUsersInBulkForm
          isForSxAdmin={false}
          accountId={accountId ?? ''}
          onUserListChange={this.handleUserListChange}
        />
      </div>
    );
  }

  private renderContent() {
    switch(this.state.step) {
    case ModalSteps.Seats:
      return this.renderAddSeats();
    case ModalSteps.Summary:
      return this.renderSummary();
    case ModalSteps.Users:
      return this.renderAddUsersInBulk();
    }
  }

  render (): React.ReactNode {
    const { step } = this.state;
    const { onCancel, currentSeats } = this.props;
    const isClosable = step !== ModalSteps.Users;

    if (!currentSeats || !isTrialsOfTiersEnabled) {
      return null;
    }

    return (
      <Modal
        visible
        className={styles.modal}
        closeIcon={isClosable ? <Icon component={CrossSVG} /> : undefined}
        onCancel={onCancel}
        centered
        closable={isClosable}
        maskClosable
        width={500}
        footer={null}
      >
        <div className={styles.container}>
          {this.renderAlert()}
          {this.renderTitle()}
          {this.renderContent()}
          {this.renderFooter()}
        </div>
      </Modal>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  const user = authSelectors.getLoggedInUser(state);
  const account = getAccountById(state, user?.account.id || '');
  const currentSeats = account?.seatsPurchased || 0;
  const renewalDate = account?.renewalAt ? moment.utc(account.renewalAt).format('MMM D, Y') : '--';
  const isFetchingProratedEnterpriseLicenseCost = selectLoadingPreviewProratedEnterpriseLicenseCost(state);
  const proratedEnterpriseLicenseCost = selectPreviewProratedEnterpriseLicenseCost(state);
  const isFetchingAccount = getLoading(state);
  const isAddingEnterpriseSeats = selectLoadingAddEnterpriseSeats(state);
  const billingEmail = account?.billingAddress?.email;
  const isSendingInvites = getBulkLoading(state);

  return {
    accountId: account?.id,
    currentSeats,
    renewalDate,
    billingEmail,
    isFetchingAccount,
    isSendingInvites,
    isAddingEnterpriseSeats,
    proratedEnterpriseLicenseCost,
    isFetchingProratedEnterpriseLicenseCost
  };
};

const mapDispatchToProps: IDispatchProps = {
  resetProratedEnterpriseLicenseCostValues: resetValues,
  createUsersInBulk: createUsersInBulk,
  fetchPreviewProratedEnterpriseLicenseCost: fetchPreviewProratedEnterpriseLicenseCost,
  addEnterpriseSeats: addEnterpriseSeats
};

const AddSeatsEnterpriseModalWithRouter = connect(mapStateToProps, mapDispatchToProps)(AddSeatsEnterpriseModalBase);

export const AddSeatsEnterpriseModal = withRouter(AddSeatsEnterpriseModalWithRouter);
